// Sesión del panel: quien entra es una persona del equipo Glubi, verificado por la API contra glubi.personal_glubi.
// Un correo de cliente puede iniciar sesión en Supabase, pero acá no ve nada: el panel se lo dice y se cierra.
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { supabase } from './supabase';

interface Yo { usuario: { id: string; correo: string | null }; personal: boolean; hoy: string }

interface ValorSesion {
  sesion: Session | null;
  listo: boolean;
  yo: Yo | undefined;
  salir: () => Promise<void>;
  aviso: (texto: string) => void;
  toast: { id: number; texto: string } | null;
}

const Contexto = createContext<ValorSesion | null>(null);

export function SesionProvider({ children }: { children: ReactNode }) {
  const [sesion, setSesion] = useState<Session | null>(null);
  const [listo, setListo] = useState(false);
  const [toast, setToast] = useState<{ id: number; texto: string } | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSesion(data.session); setListo(true); });
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setSesion(s));
    return () => data.subscription.unsubscribe();
  }, []);

  const yo = useQuery({ queryKey: ['yo', sesion?.user.id], queryFn: () => api<Yo>('/api/yo'), enabled: Boolean(sesion) });

  const aviso = useCallback((texto: string) => {
    const id = Date.now();
    setToast({ id, texto });
    setTimeout(() => setToast((t) => (t?.id === id ? null : t)), 4200);
  }, []);

  const salir = useCallback(async () => { await supabase.auth.signOut(); qc.clear(); }, [qc]);

  return <Contexto.Provider value={{ sesion, listo, yo: yo.data, salir, aviso, toast }}>{children}</Contexto.Provider>;
}

export function useSesion(): ValorSesion {
  const v = useContext(Contexto);
  if (!v) throw new Error('Falta SesionProvider');
  return v;
}
